import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  BarChart3,
  Building2,
  ChevronRight,
  ClipboardList,
  FileText,
  Languages,
  LayoutDashboard,
  Menu,
  QrCode,
  SlidersHorizontal,
  Settings,
  ShieldCheck,
  Users,
  X
} from "lucide-react";
import { api, type Hotel } from "./api/client";
import { AlertsPage } from "./pages/alerts";
import { AnalyticsPage } from "./pages/analytics";
import { AuditPage } from "./pages/audit";
import { DashboardPage } from "./pages/dashboard";
import { HotelsPage } from "./pages/hotels";
import { IncidentsPage } from "./pages/incidents";
import { InviteAcceptPage } from "./pages/invite";
import { LoginPage } from "./pages/login";
import { PasswordResetPage, RequiredPasswordChangePage } from "./pages/password-reset";
import { PrivacyPage } from "./pages/privacy";
import { QrRoutesPage } from "./pages/qr-routes";
import { ReportsPage } from "./pages/reports";
import { SettingsPage } from "./pages/settings";
import { SurveyRulesPage } from "./pages/survey-rules";
import { SurveysPage } from "./pages/surveys";
import { TranslationsPage } from "./pages/translations";
import { UsersPage } from "./pages/users";

type Page = "dashboard" | "hotels" | "surveys" | "rules" | "qr" | "alerts" | "incidents" | "analytics" | "reports" | "translations" | "users" | "privacy" | "audit" | "settings";

type NavItem = { id: Page; label: string; helper: string; icon: typeof LayoutDashboard; permission?: string };

const navSections = [
  {
    label: "Pregled",
    items: [
      { id: "dashboard", label: "Dashboard", helper: "KPI i status", icon: LayoutDashboard, permission: "analytics.view" },
      { id: "analytics", label: "Analitika", helper: "Trendovi i filteri", icon: BarChart3, permission: "analytics.view" }
    ]
  },
  {
    label: "Ankete",
    items: [
      { id: "surveys", label: "Builder", helper: "Pitanja, jezici, preview", icon: ClipboardList, permission: "survey.view" },
      { id: "rules", label: "Pravila", helper: "Time i segment logika", icon: SlidersHorizontal, permission: "survey.update" },
      { id: "translations", label: "Prevodi", helper: "UI i survey import", icon: Languages, permission: "survey.translate" }
    ]
  },
  {
    label: "Objekti i QR",
    items: [
      { id: "hotels", label: "Objekti", helper: "Hoteli i branding", icon: Building2, permission: "hotel.view" },
      { id: "qr", label: "QR kodovi", helper: "Rute, download, bulk", icon: QrCode, permission: "qr.view" }
    ]
  },
  {
    label: "Operacije",
    items: [
      { id: "alerts", label: "Alerti", helper: "Recipienti i SLA", icon: AlertTriangle, permission: "alert.view" },
      { id: "incidents", label: "Incidenti", helper: "Acknowledge i resolve", icon: ShieldCheck, permission: "incident.view" },
      { id: "reports", label: "Izvjestaji", helper: "PDF i rasporedi", icon: FileText, permission: "report.view" }
    ]
  },
  {
    label: "Administracija",
    items: [
      { id: "users", label: "Korisnici", helper: "Role i objekti", icon: Users, permission: "user.view" },
      { id: "privacy", label: "Privacy", helper: "DSAR i retention", icon: ShieldCheck, permission: "privacy_request.manage" },
      { id: "audit", label: "Audit", helper: "Log i export", icon: FileText, permission: "audit.view" },
      { id: "settings", label: "Podesavanja", helper: "Feature flags", icon: Settings, permission: "settings.view" }
    ]
  }
] satisfies Array<{ label: string; items: NavItem[] }>;

const brandLogoLight = `${import.meta.env.BASE_URL}brand/aviza-logo-light.png`;

export function App() {
  const [page, setPage] = useState<Page>(() => incidentIdFromPath() ? "incidents" : "dashboard");
  const [hotel, setHotel] = useState("all");
  const [navOpen, setNavOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchLastX = useRef<number | null>(null);
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<{ user: { email: string; passwordResetRequired?: boolean; permissions?: string[] } | null }>("/api/admin/auth/me"),
    retry: false
  });
  const hotels = useQuery({
    queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels"),
    enabled: Boolean(me.data?.user && !me.data.user.passwordResetRequired)
  });

  useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", navOpen);
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setNavOpen(false);
    }
    function onTouchStart(event: TouchEvent) {
      const touch = event.touches[0];
      if (!touch || !window.matchMedia("(max-width: 900px)").matches) return;
      if (navOpen || touch.clientX <= 28) {
        touchStartX.current = touch.clientX;
        touchLastX.current = touch.clientX;
      }
    }
    function onTouchMove(event: TouchEvent) {
      if (touchStartX.current === null) return;
      const touch = event.touches[0];
      if (touch) touchLastX.current = touch.clientX;
    }
    function onTouchEnd() {
      if (touchStartX.current === null || touchLastX.current === null) return;
      const deltaX = touchLastX.current - touchStartX.current;
      if (!navOpen && deltaX > 64) setNavOpen(true);
      if (navOpen && deltaX < -64) setNavOpen(false);
      touchStartX.current = null;
      touchLastX.current = null;
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.body.classList.remove("mobile-nav-open");
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [navOpen]);

  const resetToken = resetTokenFromPath();
  if (resetToken) return <PasswordResetPage token={resetToken} onReset={() => me.refetch()} />;
  if (me.isLoading) return <div className="loading">Ucitavanje</div>;
  const inviteToken = inviteTokenFromPath();
  if (me.isError && inviteToken) return <InviteAcceptPage token={inviteToken} onAccepted={() => me.refetch()} />;
  if (me.isError || !me.data?.user) return <LoginPage onLoggedIn={() => me.refetch()} />;
  if (me.data?.user.passwordResetRequired) return <RequiredPasswordChangePage onChanged={() => me.refetch()} />;
  const permissions = new Set(me.data?.user.permissions ?? []);
  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.permission || permissions.has(item.permission))
    }))
    .filter((section) => section.items.length);

  function navigateTo(nextPage: Page) {
    setPage(nextPage);
    setNavOpen(false);
  }

  return (
    <div className={navOpen ? "app-shell wrapper nav-open" : "app-shell wrapper"}>
      <button className="mobile-nav-pull" type="button" onClick={() => setNavOpen(true)} aria-label="Otvori navigaciju">
        <ChevronRight size={18} />
      </button>
      <button className="mobile-nav-overlay" type="button" onClick={() => setNavOpen(false)} aria-label="Zatvori navigaciju" />
      <aside className="sidebar sidenav-menu" aria-label="Glavna navigacija">
        <div className="brand app-brand">
          <img className="brand-logo brand-logo-light" src={brandLogoLight} alt="" aria-hidden="true" />
          <span>Aviza</span>
          <button className="nav-close" type="button" onClick={() => setNavOpen(false)} aria-label="Zatvori navigaciju">
            <X size={18} />
          </button>
        </div>
        <nav className="side-nav">
          {visibleSections.map((section) => (
            <section className="nav-section" key={section.label}>
              <span className="nav-section-title">{section.label}</span>
              <div className="nav-section-items">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button key={item.id} className={page === item.id ? "nav-item selected" : "nav-item"} onClick={() => navigateTo(item.id)}>
                      <Icon size={18} />
                      <span>
                        <strong>{item.label}</strong>
                        <small>{item.helper}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </nav>
        <div className="sidebar-alert">
          <AlertTriangle size={18} />
          <span>Red flag email SLA: 15s</span>
        </div>
      </aside>
      <main className="workspace content-page">
        <header className="topbar app-topbar">
          <button className="mobile-nav-button" type="button" onClick={() => setNavOpen(true)} aria-label="Otvori navigaciju">
            <Menu size={20} />
          </button>
          <div>
            <h1>{titleFor(page)}</h1>
            <p>Operativni panel za Aviza: ankete, QR rute, alerte i compliance evidencije.</p>
          </div>
          <select value={hotel} onChange={(event) => setHotel(event.target.value)} aria-label="Hotel filter" hidden={page !== "dashboard" && page !== "analytics"}>
            <option value="all">Svi hoteli</option>
            {(hotels.data ?? []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </header>
        {page === "dashboard" && <DashboardPage hotelId={hotel === "all" ? "" : hotel} />}
        {page === "hotels" && <HotelsPage />}
        {page === "qr" && <QrRoutesPage />}
        {page === "surveys" && <SurveysPage />}
        {page === "rules" && <SurveyRulesPage />}
        {page === "alerts" && <AlertsPage />}
        {page === "incidents" && <IncidentsPage initialIncidentId={incidentIdFromPath()} canAssign={permissions.has("incident.assign")} />}
        {page === "analytics" && <AnalyticsPage hotelId={hotel === "all" ? "" : hotel} />}
        {page === "reports" && <ReportsPage />}
        {page === "translations" && <TranslationsPage />}
        {page === "users" && <UsersPage />}
        {page === "privacy" && <PrivacyPage />}
        {page === "audit" && <AuditPage />}
        {page === "settings" && <SettingsPage />}
      </main>
    </div>
  );
}

function inviteTokenFromPath() {
  const match = window.location.pathname.match(/^(?:\/admin)?\/invite\/([^/]+)$/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

function resetTokenFromPath() {
  const match = window.location.pathname.match(/^(?:\/admin)?\/reset-password\/([^/]+)$/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

function incidentIdFromPath() {
  return window.location.pathname.match(/^(?:\/admin)?\/incidents\/([a-f0-9-]{36})$/i)?.[1] ?? "";
}

function titleFor(page: Page) {
  return {
    dashboard: "Dashboard",
    hotels: "Objekti",
    surveys: "Survey Builder",
    rules: "Pravila anketa",
    qr: "QR rute",
    alerts: "Alerti",
    incidents: "Incidenti",
    analytics: "Analitika",
    reports: "Izvjestaji",
    translations: "Prevodi",
    users: "Korisnici",
    privacy: "Privacy Ops",
    audit: "Audit Trail",
    settings: "Podesavanja"
  }[page];
}
